import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_URL}/auth/refresh-token`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    const errorMessage = (error.response?.data as { message?: string })?.message || 'An error occurred';
    
    if (error.response?.status !== 401) {
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  register: (data: { email: string; password: string; firstName: string; lastName: string; phone?: string }) =>
    api.post('/auth/register', data),
  
  logout: () => api.post('/auth/logout'),
  
  getMe: () => api.get('/auth/me'),
  
  updateProfile: (data: { firstName?: string; lastName?: string; phone?: string }) =>
    api.patch('/auth/profile', data),
  
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.patch('/auth/change-password', data),
};

// Business API
export const businessApi = {
  getMyBusinesses: () => api.get('/businesses/my'),
  
  getBusiness: (id: string) => api.get(`/businesses/${id}`),
  
  createBusiness: (data: { name: string; description?: string; address?: string; city?: string; phone?: string; email?: string; website?: string }) =>
    api.post('/businesses', data),
  
  updateBusiness: (id: string, data: Partial<any>) =>
    api.patch(`/businesses/${id}`, data),
  
  deleteBusiness: (id: string) => api.delete(`/businesses/${id}`),
  
  getStats: (id: string) => api.get(`/businesses/${id}/stats`),
};

// Service API
export const serviceApi = {
  getBusinessServices: (businessId: string) =>
    api.get(`/services/business/${businessId}`),
  
  createService: (data: { businessId: string; name: string; description?: string; duration: number; price: number; color?: string }) =>
    api.post('/services', data),
  
  updateService: (id: string, data: Partial<any>) =>
    api.patch(`/services/${id}`, data),
  
  deleteService: (id: string) => api.delete(`/services/${id}`),
};

// Staff API
export const staffApi = {
  getBusinessStaff: (businessId: string) =>
    api.get(`/staff/business/${businessId}`),
  
  createStaff: (data: { businessId: string; firstName: string; lastName: string; email?: string; phone?: string; bio?: string }) =>
    api.post('/staff', data),
  
  updateStaff: (id: string, data: Partial<any>) =>
    api.patch(`/staff/${id}`, data),
  
  deleteStaff: (id: string) => api.delete(`/staff/${id}`),
};

// Event API
export const eventApi = {
  getBusinessEvents: (businessId: string, params?: { from?: string; to?: string }) =>
    api.get(`/events/business/${businessId}`, { params }),
  
  createEvent: (data: { businessId: string; title: string; description?: string; startTime: string; endTime: string; serviceId?: string; staffId?: string; maxCapacity?: number }) =>
    api.post('/events', data),
  
  updateEvent: (id: string, data: Partial<any>) =>
    api.patch(`/events/${id}`, data),
  
  deleteEvent: (id: string) => api.delete(`/events/${id}`),
};

// Booking API
export const bookingApi = {
  getMyBookings: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get('/bookings/my', { params }),
  
  getBusinessBookings: (businessId: string, params?: { status?: string; from?: string; to?: string; page?: number; limit?: number }) =>
    api.get(`/bookings/business/${businessId}`, { params }),
  
  createBooking: (data: { businessId: string; eventId: string; serviceId?: string; staffId?: string; notes?: string }) =>
    api.post('/bookings', data),
  
  updateBooking: (id: string, data: { status?: string; notes?: string; isPaid?: boolean }) =>
    api.patch(`/bookings/${id}`, data),
  
  deleteBooking: (id: string) => api.delete(`/bookings/${id}`),
};

// Subscription API
export const subscriptionApi = {
  getPlans: () => api.get('/subscriptions/plans'),
  
  getCurrentSubscription: () => api.get('/subscriptions/current'),
  
  createCheckout: (data: { plan: string; interval: 'monthly' | 'yearly'; successUrl: string; cancelUrl: string }) =>
    api.post('/subscriptions/checkout', data),
  
  cancelSubscription: () => api.post('/subscriptions/cancel'),
  
  reactivateSubscription: () => api.post('/subscriptions/reactivate'),
  
  getInvoices: () => api.get('/subscriptions/invoices'),
  
  createBillingPortal: (data: { returnUrl: string }) =>
    api.post('/subscriptions/billing-portal', data),
};

export default api;
